import { CatalogSession } from '@/Model/sessions/catalogTypes';
import { Application, ApplicationSubmission } from '@/Model/application/types';
import { Enrollments } from '@/Model/enrollment/types';

export interface Cart {
  cartId: number;
  userId: number;
  cartItems: CartItem[];
  promoCodeValidation: PromoCodeValidationResponse | null;
  totals: CartTotals;
}

export interface PromoCodeValidationResponse {
  valid: boolean;
  promoCode: string;
  error: string | null;
}

export interface CartTotals {
  subtotal: number;
  tax: number;
  customFeesTotal?: CustomFeesTotal;
  promoCodeDiscount: number;
  discountPercentage: number;
  total: number;
}

export interface TaxRate {
  id: string;
  displayName: string;
  percentage: number;
}

export interface CustomFeeTotal {
  sessionId: number;
  productId: string;
  name: string;
  description?: string;
  totalExcludingTax: number;
}

export interface CustomFeesTotal {
  customFees: CustomFeeTotal[];
  feesTotalExcludingTax: number;
}

export interface CustomFeesTotalLineItem extends Pick<CustomFeeTotal, 'name' | 'description'> {
  formattedCurrencyTotal: string;
}

export interface CartItem {
  cartItemId: number;
  session: CatalogSession;
  scholar?: SubmitCartScholar;
}

export interface CartScholar {
  scholarId: string;
  scholarName: string;
}

export interface GetCartResponse {
  cartId: number;
  userId: number;
  cartItems: GetCartItemResponse[];
  promoCodeValidation: PromoCodeValidationResponse | null;
  totals: CartTotals;
}

export interface GetCartItemResponse {
  cartItemId: number;
  session?: CatalogSession;
  scholar?: SubmitCartScholar;
  sessionRemoved?: boolean;
}

export interface SubmitCartScholar {
  scholarId: string | number;
  scholarFirstName: string;
  scholarLastName: string;
}

export interface RemoveCartItemRequest {
  cartItemId: number;
}

export interface AddCartItemRequest {
  cartId: number;
  sessionId: number;
  quantity: number;
}

export interface AddCartItemActionPayload {
  session: CatalogSession;
  quantity: number;
  shouldNotifyUser: boolean;
}

export interface UpdateCartItemRequest {
  cartId: number;
  sessionId: number;
  quantity: number;
  scholarIds: string[];
}

export interface UpdateCartItemActionPayload {
  updateCartItemPayload: UpdateCartItemRequest;
  session: CatalogSession;
}


export interface CartApplication {
  cartItemId: number;
  rawApplicationId: string;
  applicationInProgress?: ApplicationSubmission;
  cartItem?: CartItem;
  sessionId: number;
  elasticSubmissionId?: string;
}

export interface GetCartApplicationsResponse {
  rawApplications: Array<Application>;
  cartItemApplicationData: Array<CartApplication>;
}

export interface getCartParams {
  userId: number;
  accountId: number;
  calcCartComplete?: boolean;
  promoCode?: string;
}

export interface CartItemEnrollmentDetails {
  cartItemId: number;
  guardianUserId: number;
  scholarFirstName: string;
  scholarLastName: string;
  scholarDateOfBirth: string;
}

export enum IndexAction {
  Increment,
  Decrement,
  SeekEnd
}

export interface LoadCartApplicationsDataConfig {
  indexAction?: IndexAction;
  resetFormScholarData?: boolean;
}

export interface SubmitOrderRequest {
  cartId: number;
  accountId: number;
  setupIntentId: string | null;
  orderDetails: SubmitOrderDetail[];
  promoCode?: string;
  customCheckoutId?: number;
  autoEnrollStatusId?: number;
  customCheckoutName?: string;
}

export interface SubmitOrderDetail {
  cartItemId: number;
  sessionId: number;
  productName: string;
  priceId: string;
  taxRateId?: string;
  enrollments: Enrollments;
  price: number;
}

export interface SubmitOrderResponse {
  submitOrderResponseDetails: SubmitOrderResponseDetail[];
}

export interface SubmitOrderResponseDetail {
  cartItemId: number;
  invoiceId: string;
  status: string;
  failureMessage: string;
}

export interface SubmitCartItem {
  session: CatalogSession;
  cartItemIds: number[];
  scholars: SubmitCartScholar[];
}

export interface StorePolicy {
  enabled: boolean;
  url: string;
}

export interface AssociateScholarToCartItemRequest {
  cartItemId: number;
  guardianUserId: number;
  scholarGuid?: string;
  scholarFirstName?: string;
  scholarLastName?: string;
  scholarDateOfBirth?: string;
}

export interface SetCartPayload {
  cartResponse: GetCartResponse;
  shouldResetApplications: boolean;
}

// Custom checkout object ,used for custom payments defined on the admin site
export interface CustomCheckoutOption {
  id: number;
  accountID: number;
  name: string;
  instructions: string;
  autoEnrollStatusId: number;
  isEnabled: boolean;
}

// selected payment option handled at submit time , default is online payment
// Added string values since we are handling string on the components
export enum PaymentOptions {
  ONLINE_PAYMENT = '0',
  CUSTOM_PAYMENT = '1'
}